// ═══════════════════════════════════════════════════════════════════════════
// BUDGET TAB - UrbanQ v1.9.6
// Presupuesto mensual con barras de progreso y status
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
    FiTarget, FiRefreshCw, FiSave, FiAlertCircle,
    FiCheckCircle, FiAlertTriangle
} from 'react-icons/fi';

export default function BudgetTab({ year, month, token, apiUrl, scope, onNotification }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [budgets, setBudgets] = useState([]);
    const [summary, setSummary] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(month);
    const [hasChanges, setHasChanges] = useState(false);

    const months = [
        { value: 1, label: 'Enero' },
        { value: 2, label: 'Febrero' },
        { value: 3, label: 'Marzo' },
        { value: 4, label: 'Abril' },
        { value: 5, label: 'Mayo' },
        { value: 6, label: 'Junio' },
        { value: 7, label: 'Julio' },
        { value: 8, label: 'Agosto' },
        { value: 9, label: 'Septiembre' },
        { value: 10, label: 'Octubre' },
        { value: 11, label: 'Noviembre' },
        { value: 12, label: 'Diciembre' }
    ];

    const fetchBudget = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${apiUrl}/api/financial/budget?year=${year}&month=${selectedMonth}&scope=${scope}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            const result = await response.json();
            if (result.success) {
                setBudgets(result.data || []);
                setSummary(result.summary || null);
            }
        } catch (err) {
            console.error('Error fetching budget:', err);
            onNotification?.('Error al cargar presupuesto', 'error');
        } finally {
            setLoading(false);
        }
    }, [year, selectedMonth, token, apiUrl, scope, onNotification]);

    useEffect(() => {
        fetchBudget();
    }, [fetchBudget]);

    const formatCurrency = (amount) => {
        return `$${parseFloat(amount || 0).toFixed(2)}`;
    };

    const handleBudgetChange = (categoryId, value) => {
        setBudgets(prev => prev.map(b =>
            b.category_id === categoryId
                ? { ...b, budget_amount: parseFloat(value) || 0 }
                : b
        ));
        setHasChanges(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const response = await fetch(`${apiUrl}/api/financial/budget`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    budgets: budgets.map(b => ({
                        category_id: b.category_id,
                        amount: b.budget_amount
                    })),
                    year,
                    month: selectedMonth,
                    scope
                })
            });

            const result = await response.json();
            if (result.success) {
                onNotification?.('Presupuesto guardado', 'success');
                setHasChanges(false);
                fetchBudget();
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            console.error('Error saving budget:', err);
            onNotification?.('Error al guardar', 'error');
        } finally {
            setSaving(false);
        }
    };

    const getProgressClass = (percentage) => {
        if (percentage >= 1) return 'danger';
        if (percentage >= 0.9) return 'warning';
        return 'ok';
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'ok':
                return <FiCheckCircle />;
            case 'alerta':
                return <FiAlertCircle />;
            case 'excedido':
                return <FiAlertTriangle />;
            default:
                return <span>-</span>;
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'ok':
                return 'ok';
            case 'alerta':
                return 'warning';
            case 'excedido':
                return 'danger';
            default:
                return 'none';
        }
    };

    if (loading) {
        return (
            <div className="urbanq-finanzas-loading">
                <div className="urbanq-finanzas-spinner"></div>
            </div>
        );
    }

    return (
        <div className="urbanq-finanzas-budget-tab">
            <div className="urbanq-finanzas-section">
                <div className="urbanq-finanzas-section-header">
                    <h2><FiTarget /> Presupuesto Mensual</h2>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div className="urbanq-finanzas-filter-group">
                            <span className="urbanq-finanzas-filter-label">Mes:</span>
                            <select
                                className="urbanq-finanzas-select"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                            >
                                {months.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            className={`urbanq-finanzas-btn ${hasChanges ? 'success' : 'secondary'}`}
                            onClick={handleSave}
                            disabled={saving || !hasChanges}
                        >
                            <FiSave /> {saving ? 'Guardando...' : 'Guardar'}
                        </button>

                        <button className="urbanq-finanzas-btn-icon" onClick={fetchBudget}>
                            <FiRefreshCw />
                        </button>
                    </div>
                </div>

                {/* Resumen */}
                {summary && (
                    <div className="urbanq-finanzas-totals-bar" style={{ marginBottom: '20px' }}>
                        <div className="urbanq-finanzas-totals-item">
                            <span className="urbanq-finanzas-totals-label">Presupuesto Total:</span>
                            <span className="urbanq-finanzas-totals-value">
                                {formatCurrency(summary.totalBudget)}
                            </span>
                        </div>
                        <div className="urbanq-finanzas-totals-item">
                            <span className="urbanq-finanzas-totals-label">Gastado:</span>
                            <span className="urbanq-finanzas-totals-value negative">
                                {formatCurrency(summary.totalSpent)}
                            </span>
                        </div>
                        <div className="urbanq-finanzas-totals-item">
                            <span className="urbanq-finanzas-totals-label">Diferencia:</span>
                            <span className={`urbanq-finanzas-totals-value ${summary.totalDifference >= 0 ? 'positive' : 'negative'}`}>
                                {formatCurrency(summary.totalDifference)}
                            </span>
                        </div>
                    </div>
                )}

                {/* Tabla de presupuesto */}
                <div className="urbanq-finanzas-table-wrapper">
                    {budgets.length > 0 ? (
                        <table className="urbanq-finanzas-table grid-lines">
                            <thead>
                                <tr>
                                    <th style={{ width: '180px' }}>Categoria</th>
                                    <th className="numeric" style={{ width: '130px' }}>Presupuesto</th>
                                    <th className="numeric" style={{ width: '120px' }}>Gastado</th>
                                    <th className="numeric" style={{ width: '120px' }}>Diferencia</th>
                                    <th style={{ minWidth: '200px' }}>Progreso</th>
                                    <th style={{ width: '80px', textAlign: 'center' }}>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {budgets.map(budget => {
                                    const percentage = budget.percentage || 0;
                                    const progressWidth = Math.min(percentage * 100, 100);
                                    const progressClass = getProgressClass(percentage);

                                    return (
                                        <tr key={budget.category_id}>
                                            <td>
                                                <span
                                                    className="urbanq-finanzas-category-badge"
                                                    style={{
                                                        background: `${budget.color}20`,
                                                        color: budget.color
                                                    }}
                                                >
                                                    {budget.category}
                                                </span>
                                            </td>

                                            <td className="numeric">
                                                <div className="urbanq-finanzas-amount-input" style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span style={{ marginRight: '5px', color: '#718096' }}>$</span>
                                                    <input
                                                        type="number"
                                                        value={budget.budget_amount || ''}
                                                        onChange={(e) => handleBudgetChange(budget.category_id, e.target.value)}
                                                        className="urbanq-finanzas-form-input"
                                                        style={{
                                                            width: '100px',
                                                            padding: '8px',
                                                            textAlign: 'right'
                                                        }}
                                                        step="0.01"
                                                        min="0"
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                            </td>

                                            <td className="numeric amount negative">
                                                {formatCurrency(budget.actual_spent)}
                                            </td>

                                            <td className={`numeric amount ${budget.difference >= 0 ? 'positive' : 'negative'}`}>
                                                {formatCurrency(budget.difference)}
                                            </td>

                                            <td className="urbanq-finanzas-progress-cell">
                                                <div className="urbanq-finanzas-progress-bar">
                                                    <div
                                                        className={`urbanq-finanzas-progress-fill ${progressClass}`}
                                                        style={{ width: `${progressWidth}%` }}
                                                    >
                                                        {progressWidth >= 30 && (
                                                            <span className="urbanq-finanzas-progress-text">
                                                                {(percentage * 100).toFixed(0)}%
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {progressWidth < 30 && (
                                                    <span className="urbanq-finanzas-progress-text-outside">
                                                        {(percentage * 100).toFixed(0)}%
                                                    </span>
                                                )}
                                            </td>

                                            <td>
                                                <div className={`urbanq-finanzas-status ${getStatusClass(budget.status)}`}>
                                                    {getStatusIcon(budget.status)}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className="urbanq-finanzas-empty">
                            <FiTarget className="urbanq-finanzas-empty-icon" />
                            <h3>No hay categorias de gastos</h3>
                            <p>Configura categorias en la pestana de Configuracion</p>
                        </div>
                    )}
                </div>

                {/* Leyenda */}
                <div style={{ marginTop: '20px', display: 'flex', gap: '25px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className="urbanq-finanzas-status ok"><FiCheckCircle /></div>
                        <span style={{ fontSize: '13px', color: '#718096' }}>OK (menos del 90%)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className="urbanq-finanzas-status warning"><FiAlertCircle /></div>
                        <span style={{ fontSize: '13px', color: '#718096' }}>Alerta (90-100%)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className="urbanq-finanzas-status danger"><FiAlertTriangle /></div>
                        <span style={{ fontSize: '13px', color: '#718096' }}>Excedido (mas del 100%)</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
